'use client'

import DOMPurify from 'dompurify'
import React from 'react'

import { iconsMap } from '@/constants/icons'
import { MLink } from '@/core/MLink'
import { THEME_COLORS_ENUM } from '@/types'
import { cn } from '@/utils/cn'

import { HERO_CARD_COLOR_CONFIG } from './_constants/colors'

import type { IHeroCardAProps } from './_types/types'

/**
 * MHeroCardA Component
 *
 * Reusable hero card component that adapts responsively between mobile and desktop layouts.
 * Can be used in admin preview and on the real website.
 * Compatible with both Next.js SSR and plain React applications.
 * Works as both Server Component and Client Component (no useState).
 *
 * @example Basic usage (responsive by default)
 * ```tsx
 * <MHeroCardA
 *   title="<p>Nová posila v tíme<br><strong>pediatrie</strong></p>"
 *   text="<p>Od nového roka medzi nami vítame <strong>pediatričku MUDr. Zuzanu Čajku</strong>.</p>"
 *   buttonText="Zistite viac"
 *   buttonUrl="/tim/zuzana-cajka"
 *   colorTheme={THEME_COLORS_ENUM.PINK}
 * />
 * ```
 */
export const MHeroCardA: React.FC<IHeroCardAProps> = ({
	className,
	title,
	text,
	buttonText,
	buttonUrl,
	colorTheme,
	icon
}) => {
	const colors =
		HERO_CARD_COLOR_CONFIG[colorTheme as keyof typeof HERO_CARD_COLOR_CONFIG] ||
		HERO_CARD_COLOR_CONFIG[THEME_COLORS_ENUM.PINK]

	// Sanitize HTML content (only in browser environment)
	const sanitizedTitle = typeof window !== 'undefined' ? DOMPurify.sanitize(title) : title
	const sanitizedText = typeof window !== 'undefined' ? DOMPurify.sanitize(text) : text

	const rootClassName = cn(
		'm-hero-card-a',
		buttonUrl ? undefined : 'm-hero-card-a--no-link',
		className
	)
	const cssVars = {
		['--button-bg' as const]: colors.buttonBackground,
		['--button-color' as const]: colors.textColor,
		['--button-hover-bg' as const]: colors.buttonHoverBackground,
		['--button-hover-color' as const]: colors.buttonHoverText
	} as React.CSSProperties

	const cardContent = (
		<div className='m-hero-card-a__content' style={{ backgroundColor: colors.cardBackground }}>
			{icon && iconsMap[icon] && (
				<div className='m-hero-card-a__icon' aria-hidden>
					<img src={iconsMap[icon]} alt='' className='m-hero-card-a__icon-img' />
				</div>
			)}

			<div className='m-hero-card-a__text-wrapper'>
				<div
					className='m-hero-card-a__title'
					style={{ color: colors.textColor }}
					dangerouslySetInnerHTML={{ __html: sanitizedTitle }}
				/>

				<div
					className='m-hero-card-a__text'
					style={{ color: colors.textColor }}
					dangerouslySetInnerHTML={{ __html: sanitizedText }}
				/>
			</div>
		</div>
	)

	if (
		buttonUrl &&
		buttonUrl?.trim()?.length > 0 &&
		buttonText &&
		buttonText?.trim()?.length > 0
	) {
		return (
			<MLink
				className={rootClassName}
				title={buttonText}
				aria-label={buttonText}
				href={buttonUrl}
				style={cssVars}
			>
				{cardContent}
				<div
					className='m-hero-card-a__button'
					style={{
						backgroundColor: colors.buttonBackground,
						color:
							'buttonTextColor' in colors ? colors.buttonTextColor : colors.textColor
					}}
				>
					{buttonText}
				</div>
			</MLink>
		)
	}

	return <div className={rootClassName}>{cardContent}</div>
}

MHeroCardA.displayName = 'MHeroCardA'
